import type { RenderContext, SectionProps } from "../context";
import { directionsUrl, groupedHours, mapsEmbedUrl } from "../helpers";
import { Icon } from "../icons";
import { BUTTON, Container, EditorHint, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

function AddressCard({ ctx, className }: { ctx: RenderContext; className?: string }) {
  const { address, city } = ctx.site.contact;
  const directions = directionsUrl(ctx.site.contact);
  const hours = groupedHours(ctx.site.hours).slice(0, 3);
  return (
    <div className={cx("baze-reveal rounded-site-lg border border-site-border bg-site-surface p-6 shadow-site-md @3xl:p-8", className)}>
      <div className="flex gap-4">
        <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-site-primary">
          <Icon name="map-pin" className="size-5" />
        </span>
        <address className="not-italic">
          <span className="block font-semibold text-site-text">{ctx.site.name}</span>
          {address ? <span className="mt-1 block leading-relaxed text-site-muted">{address}</span> : null}
          {city && !(address ?? "").includes(city) ? <span className="block text-site-muted">{city}</span> : null}
        </address>
      </div>
      {hours.length > 0 ? (
        <div className="mt-6 flex gap-4 border-t border-site-border pt-6">
          <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-site-primary">
            <Icon name="clock" className="size-5" />
          </span>
          <ul className="flex-1 space-y-1 text-sm">
            {hours.map((g) => (
              <li key={g.label} className="flex justify-between gap-4">
                <span className="text-site-muted">{g.label}</span>
                <span className="font-medium text-site-text tabular-nums">{g.value}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {directions ? (
        <SiteLink ctx={ctx} href={directions} className={cx(BUTTON.primary, "mt-7 w-full")}>
          <Icon name="navigation" className="size-4" />
          Petunjuk arah
        </SiteLink>
      ) : null}
    </div>
  );
}

export function Location({ node, ctx }: SectionProps<"location">) {
  const c = node.content;
  const contact = ctx.site.contact;
  const hasPlace = !!(contact.address || contact.geo || contact.city);
  if (!hasPlace && !ctx.editor) return null;
  const embed = node.variant === "map" && c.showMap !== false ? mapsEmbedUrl(contact) : null;
  const hint = !hasPlace ? <EditorHint ctx={ctx}>Alamat belum diisi. Atur di Info bisnis → Alamat.</EditorHint> : null;

  if (embed) {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          <div className="relative mt-(--site-gap) @3xl:mt-(--site-gap-lg)">
            <div className="baze-reveal-pop aspect-[4/3] overflow-hidden rounded-site-lg border border-site-border bg-site-surface shadow-site-sm @3xl:aspect-[16/7]">
              <iframe
                src={embed}
                title={`Peta lokasi ${ctx.site.name}`}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                className="h-full w-full border-0"
                {...(ctx.editor ? { "data-baze-inert": "" } : {})}
              />
            </div>
            <AddressCard ctx={ctx} className="relative -mt-10 mx-3 @3xl:absolute @3xl:top-6 @3xl:left-6 @3xl:m-0 @3xl:w-[22rem]" />
          </div>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className="grid gap-10 @4xl:grid-cols-2 @4xl:items-center @4xl:gap-16">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          {hint ?? <AddressCard ctx={ctx} />}
        </div>
      </Container>
    </SectionShell>
  );
}
